import type { ModelError } from "@iot-platform/core";
import { NonRecuperableModelError } from "@iot-platform/core";
import type { SpatialLevelsDto } from "@station/data/dto/spatial-level.dto";
import {
    SpatialLevelDto,
    SpatialLevelsDtoSchema,
} from "@station/data/dto/spatial-level.dto";
import type { SpatialLevelUpdate } from "@station/domain/models/spatial-level.model";
import {
    SpatialLevel,
    SpatialLevels,
    SpatialLevelUpdateSchema,
} from "@station/domain/models/spatial-level.model";
// eslint-disable-next-line boundaries/dependencies
import type { SpatialLevelModalFormValuesValidated } from "@admin/presentation/components/spatial-level-modal/spatial-level-modal-form.view";
import { Station } from "@station/domain/models/station.model";
import { injectable } from "inversify";
import type { Result } from "neverthrow";
import { err, ok } from "neverthrow";
import type * as z from "zod";

@injectable()
export class SpatialLevelMapper {
    map(
        spatialLevelDto: SpatialLevelDto,
    ): Result<SpatialLevel, ModelError<typeof SpatialLevel>> {
        const NUM_TWO = 2;

        const spatialLevelDtoParsed =
            SpatialLevelDto.safeParse(spatialLevelDto);

        if (!spatialLevelDtoParsed.success) {
            console.error(spatialLevelDtoParsed.error.issues);
            return err(new NonRecuperableModelError());
        }

        const { stations, children = [] } = spatialLevelDto;

        const mappedStations = stations.map((stationDto) => {
            const stationProps = {
                id: stationDto.id,
                code: stationDto.code,
                name: stationDto.name,
                technicalName: stationDto.technical_name ?? "",
                latitude: stationDto.latitude ?? "",
                longitude: stationDto.longitude ?? "",
                radius: stationDto.radius ?? 0,
                type: stationDto.type,
                oldThresholdHours: stationDto.old_threshold_hours,
                clientCompany: stationDto.client_company ?? "",
                icon: stationDto.icon,
                lastFrameAt: stationDto.last_frame_at ?? "",
                lastFrameTimestamp: stationDto.last_frame_timestamp ?? "",
                vehicleType: stationDto.vehicle_type ?? "",
                allowDrainageReport: stationDto.allow_drainage_report,
                decisionSupportSystems:
                    stationDto.decision_support_systems?.map((dss) => ({
                        id: dss.id,
                        configuration: {
                            id: dss.configuration.id,
                            type: dss.configuration.type,
                            name: dss.configuration.name,
                            description: dss.configuration.description ?? "",
                        },
                    })) ?? [],
                pois:
                    stationDto.pois?.map((poi) => ({
                        id: poi.id,
                        code: poi.code,
                        name: poi.name,
                        technicalName: poi.technical_name,
                        type: poi.type,
                    })) ?? [],
                batteryLevel: stationDto.battery_percentage
                    ? Number(stationDto.battery_percentage.toFixed(NUM_TWO))
                    : null,
                campaign: stationDto.campaign_active
                    ? {
                          id: stationDto.campaign_active.id,
                          name: stationDto.campaign_active.name,
                          cropType: {
                              id: stationDto.campaign_active.crop_type.id,
                              name: stationDto.campaign_active.crop_type.code,
                          },
                      }
                    : null,
            };

            return Station.createWithRecovery(stationProps);
        });

        const validStations = mappedStations.mapNotNull((result) =>
            result.match(
                (station) => station,
                (error) => {
                    if (!error.recuperable) {
                        console.error(
                            "Non-recoverable error mapping station:",
                            error,
                        );
                    }
                    return null;
                },
            ),
        );

        const mappedChildren = children.map((childDto) => this.map(childDto));

        const validChildren = mappedChildren.mapNotNull((result) =>
            result.match(
                (child) => child,
                (error) => {
                    if (!error.recuperable) {
                        console.error(
                            "Non-recoverable error mapping child spatial level:",
                            error,
                        );
                    }
                    return null;
                },
            ),
        );

        return SpatialLevel.createWithRecovery({
            id: spatialLevelDto.id,
            name: spatialLevelDto.name,
            toLabel: spatialLevelDto.to_label,
            stations: validStations,
            children: validChildren,
        });
    }

    mapList(spatialLevelsDto: SpatialLevelsDto): SpatialLevels {
        const spatialLevelsDtoParsed =
            SpatialLevelsDtoSchema.safeParse(spatialLevelsDto);

        if (!spatialLevelsDtoParsed.success) {
            console.error(spatialLevelsDtoParsed.error.issues);
            return SpatialLevels.empty();
        }

        const spatialLevels = spatialLevelsDto.mapNotNull((spatialLevelDto) =>
            this.map(spatialLevelDto).match(
                (spatialLevel) => spatialLevel,
                (error) => {
                    if (!error.recuperable) {
                        console.error(
                            "Non-recoverable error mapping spatial level:",
                            error,
                        );
                    }
                    return null;
                },
            ),
        );

        return SpatialLevels.create({
            spatialLevels,
            count: spatialLevels.length,
        }).unwrapOr(SpatialLevels.empty());
    }

    mapFormToEdit(
        id: number,
        spatialLevel: SpatialLevelModalFormValuesValidated,
    ): Result<SpatialLevelUpdate, z.ZodError<SpatialLevelUpdate>> {
        const maybeSpatialLevelUpdate = SpatialLevelUpdateSchema.safeParse({
            id,
            name: spatialLevel.name,
        } satisfies SpatialLevelUpdate);

        return maybeSpatialLevelUpdate.success
            ? ok(maybeSpatialLevelUpdate.data)
            : err(maybeSpatialLevelUpdate.error);
    }
}
